import type { ActionRow, ActionType } from "../../api/contracts";
import { qs } from "../api/client";

export interface ActivityFilters { blvdId: string; staff: string; from: string; to: string; type: "" | ActionType; mode: "" | "shadow" | "live"; page: number }
export const PAGE_SIZE = 100;
export const EMPTY_ACTIVITY: ActivityFilters = { blvdId: "", staff: "", from: "", to: "", type: "", mode: "", page: 0 };

/** The API filters by staff id only; the staff box matches names on the loaded page instead. */
export function buildActionsQuery(f: ActivityFilters): string {
  return qs({ blvdId: f.blvdId, from: f.from, to: f.to, type: f.type, mode: f.mode, limit: PAGE_SIZE, offset: f.page * PAGE_SIZE });
}

export const ACTION_LABELS: Record<ActionType, string> = {
  create: "BTB placed", remove: "BTB removed", resize: "BTB resized", release: "Left to staff", lock: "Locked as DNB", adopt: "Took over BTB",
  would_create: "Would place BTB", would_remove: "Would remove BTB", would_resize: "Would resize BTB", would_release: "Would leave to staff", would_lock: "Would lock as DNB", would_adopt: "Would take over BTB", skip: "No change",
};
export const ACTION_TONE: Record<ActionType, "success" | "danger" | "info" | "neutral" | "warning"> = {
  create: "success", remove: "danger", resize: "warning", release: "neutral", lock: "info", would_create: "success", would_remove: "danger", would_resize: "warning", would_release: "neutral", would_lock: "info", adopt: "info", would_adopt: "info", skip: "neutral",
};

export function filterByStaff<T extends { staffName: string }>(rows: T[], staff: string): T[] {
  const q = staff.trim().toLowerCase();
  return q ? rows.filter((r) => r.staffName.toLowerCase().includes(q)) : rows;
}

/** Anything but "No change": the rows the "Changes only" switch keeps. */
export const isChange = (type: ActionType): boolean => type !== "skip";

export interface ActivityPass<T> { key: string; at: string; rows: T[] }
type PassRow = Pick<ActionRow, "id" | "jobId" | "createdAt">;

/**
 * Rows grouped by planning pass, in the order given (newest first from the API): consecutive rows that
 * share a job id, or, for rows without one, that were written in the same minute. A group header shows
 * the pass's time and a tally, so an owner reads "the 11:07 pass checked 5 blocks" instead of 5 bare rows.
 */
export function groupByPass<T extends PassRow>(rows: T[]): ActivityPass<T>[] {
  const out: ActivityPass<T>[] = [];
  const passKey = (r: T) => r.jobId ?? `t:${r.createdAt.slice(0, 16)}`;
  for (const r of rows) {
    const key = passKey(r);
    const last = out[out.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else out.push({ key, at: r.createdAt, rows: [r] });
  }
  return out;
}

const FAMILY: { test: (t: ActionType) => boolean; word: string }[] = [
  { test: (t) => t === "create" || t === "would_create", word: "placed" },
  { test: (t) => t === "resize" || t === "would_resize", word: "resized" },
  { test: (t) => t === "remove" || t === "would_remove", word: "removed" },
  { test: (t) => t === "lock" || t === "would_lock", word: "locked as DNB" },
  { test: (t) => t === "adopt" || t === "would_adopt", word: "taken over" },
  { test: (t) => t === "release" || t === "would_release", word: "left to staff" },
  { test: (t) => t === "skip", word: "left alone" },
];

/** "5 blocks checked · 1 resized · 4 left alone": one entry per outcome that happened in the pass. */
export function summarizePass(rows: { type: ActionType }[]): string {
  const n = rows.length;
  const parts = FAMILY.map((f) => ({ word: f.word, count: rows.filter((r) => f.test(r.type)).length })).filter((p) => p.count > 0).map((p) => `${p.count} ${p.word}`);
  return [`${n} ${n === 1 ? "block" : "blocks"} checked`, ...parts].join(" · ");
}

/** "10 blocks · 3 changed", for the page's loaded rows. */
export function summarizeLoaded(rows: { type: ActionType }[]): string {
  const n = rows.length, changed = rows.filter((r) => isChange(r.type)).length;
  return `${n} ${n === 1 ? "block" : "blocks"} · ${changed} changed`;
}
